/**
 * 룰 버전 절 — 읽기 전용 목록 (순수 프리젠테이션 오거니즘).
 *
 * @spec:2026-09-25-처방검토-관리자룰화면-룰버전-UI (Phase 1 + 보정 7a·7d·7e·7e′)
 * 계획: `docs/ai-discussions/20260926-dose-rule-버전절-읽기전용-phase1.md`
 *
 * 왜 있나 — 2026-09-22 전량 재추출이 약사의 검수 판정·메모를 덮어썼다. 백엔드가 버전을 누적해
 * DB엔 옛 버전이 남았지만 화면이 없어 약사는 여전히 못 본다. 이 절이 그 창이다.
 *
 * 비즈니스 로직 없음 — 쿼리 결과를 props로 받는다(ARCHITECTURE §2). 선별·포맷은 전부
 * `utils/doseRule.ts`의 순수 함수다.
 *
 * ## 한 버전 = 두 줄 묶음 (보정 7a)
 * 윗줄에 **버전·상태·은퇴 시각·검수자**, 아랫줄에 **전폭(`colSpan`) 보조 줄**로 메모·사유를 둔다.
 * 6칸 한 줄로 그리던 이전 구현은 1280px에서 메모를 `소아 1…`, 사유를 `재추…`로 보여줬다 —
 * 은퇴 시각이 `formatDateTime` 전문을 요구해(설계 결정 15) 그 칸이 폭을 먹고, 남은 가변 칸이
 * 각 45px 남짓으로 쪼그라들기 때문이다. 칸 폭 조정으로는 산술적으로 풀리지 않아 줄을 나눴다.
 * 보조 줄의 두 값은 **각각 최대 2줄**로 자르고 전문은 `title`에 싣는다.
 *
 * ## Phase 2 승격 지점
 * **버전 줄은 지금 비대화형이다**(`onClick`·pointer cursor 없음). 과거 버전 **본문** 열람이
 * 없는 단계에서 줄을 눌리게 만들면 "눌러도 안 열리는 목록"이 된다. Phase 2가 승격할 곳은
 * 아래 줄 렌더(`<Row>`)이고, 그때 래퍼를 `atoms/Button`으로 바꿀지도 함께 판단한다.
 * 쓰지 않을 `onRowClick` prop을 미리 만들어 두지 않는다(계획 F-1·F-2).
 *
 * ## 사유 칸의 네 가지 표시를 섞지 않는다
 * - 메모 칸: 값 없으면 `메모 없음`(흐리게)
 * - 사유 칸: 사유 노트가 없으면 **빈 칸** — 문구를 만들지 않는다
 * - 사유 칸: **조회 진행 중이면 `…`**(보정 7d). 비활성(idle)에는 쓰지 않는다 — 버전 1개 룰은
 *   `notes`를 아예 요청하지 않으므로 `…`가 뜨면 거짓말이다
 * - 사유 칸: **`notes` 조회 실패면 `—`** + 절 머리에 오류·재시도
 * 실패를 "사유 없음"으로 읽히게 하면 약사가 "또 지워졌다"로 받는다 — 이 절의 존재 이유와
 * 정면 충돌이다(계획 §7).
 */
import React, { useState } from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import Spinner from "components/atoms/Spinner";
import Button from "components/atoms/Button";
import {
  AdminDoseRuleNote,
  AdminDoseRuleVersionListItem,
} from "services/api/doseRules/types";
import type { RetireReasonEntry } from "utils/doseRule";
import {
  NOTES_LOADING_TEXT,
  NOTES_LOAD_ERROR_TEXT,
  RETRY_TEXT,
  VERSION_COLUMNS,
  VERSION_CURRENT_TEXT,
  VERSION_EMPTY_TEXT,
  VERSION_LOAD_ERROR_TEXT,
  VERSION_NOTE_EMPTY_TEXT,
  VERSION_NOTE_LABEL,
  VERSION_REASON_LABEL,
  VERSION_SECTION_TITLE,
  buildRetireReasonMap,
  formatDateTime,
  formatVersionNumber,
  isCurrentVersionRow,
  retireReasonEntries,
  retireReasonText,
  retireRepeatTitle,
  versionStatusLabel,
} from "utils/doseRule";

export interface DoseRuleVersionListProps {
  /** 버전 목록 응답. **서버가 `ORDER BY version DESC`로 준 순서를 그대로 그린다.** */
  versions?: AdminDoseRuleVersionListItem[];
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  /** 노트 전량(선별은 이 컴포넌트가 author 화이트리스트로 한다). */
  notes?: AdminDoseRuleNote[];
  /**
   * 노트 조회가 **실제로 진행 중**인지 — 사유 칸이 `…`가 된다(보정 7d).
   * 비활성(idle)을 여기에 넣으면 안 된다. 버전 1개 룰은 `notes`를 요청조차 하지 않는다.
   */
  isNotesLoading?: boolean;
  /** 노트 조회만 실패했는지 — 사유 칸이 `—`가 되고 절 머리에 오류가 붙는다. */
  isNotesError?: boolean;
  onNotesRetry?: () => void;
}

const Fold = styled.details`
  margin: 12px 0;
  border-top: 1px solid ${COLOR_SYSTEM.get("Gray")?.[50]};
  padding-top: 8px;

  summary {
    cursor: pointer;
    font-size: 12px;
    font-weight: 700;
    color: ${COLOR_SYSTEM.get("Gray")?.[500]};
  }
`;

const ErrorLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 6px;
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Red")?.[700]};
`;

const TableWrap = styled.div`
  overflow-x: auto;
  margin-top: 8px;
`;

const Table = styled.table`
  width: 100%;
  /* min-width를 주지 않는다. 좌측 패널(기본 46%)보다 넓어지면 TableWrap이 가로 스크롤한다.
     윗줄은 네 칸뿐이고 이 절의 핵심 산출물(메모·사유)은 전폭 보조 줄로 내려갔으므로,
     기본 비율에서는 어느 칸도 잘리지 않는다(보정 7a). */
  border-collapse: collapse;
  font-size: 12px;
  table-layout: fixed;
`;

const Th = styled.th`
  text-align: left;
  padding: 6px 8px;
  white-space: nowrap;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
  font-weight: 600;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[50]};
`;

/**
 * 한 버전 = `<tbody>` 하나 = 두 줄(보정 7a). 묶음을 DOM 구조로 남겨 보조기술이 윗줄과 보조 줄을
 * 한 덩어리로 읽는다. 줄 사이 구분선은 묶음의 **마지막** 줄에만 그린다.
 */
const Group = styled.tbody``;

/**
 * 버전 윗줄. **`cursor: pointer`를 주지 않는다** — 비대화형임을 스타일로도 못 박는다.
 * 현재 버전 줄은 굵게: 약사가 이 절에서 가장 먼저 찾는 것이 "지금 판정에 쓰이는 줄"이고,
 * `현재` 글자 하나로는 스캔이 안 된다(계획 J-3).
 */
const Row = styled.tr<{ $current?: boolean }>`
  font-weight: ${({ $current }) => ($current ? 700 : 400)};
  color: ${({ $current }) =>
    $current ? COLOR_SYSTEM.get("Gray")?.[900] : COLOR_SYSTEM.get("Gray")?.[700]};
`;

const Td = styled.td`
  padding: 6px 8px 2px;
  vertical-align: top;
  white-space: nowrap;
  /* 칸 밖으로 흘러 옆 칸 위에 겹쳐 그려지는 것을 막는다 —
     table-layout: fixed 는 폭만 정할 뿐 유출을 막지 않는다. */
  overflow: hidden;
`;

/**
 * 은퇴 시각 칸. **말줄임을 걸지 않는다**(보정 7a) — `formatDateTime` 전문
 * (`2026. 09. 22. 오전 11:39`)이 통째로 보여야 한다. 같은 분에 26건이 몰려 있어
 * 분 단위까지 있어야 구분이 되고(화면 설계 5), 짧은 포맷터는 설계 결정 15가 금지한다.
 * 폭은 아래 `COLUMN_WIDTHS`가 이 문자열에 맞춰 잡는다.
 */
const TimeTd = styled(Td)`
  text-overflow: clip;
`;

/** 검수자 칸 — Cognito UUID 36자라 한 줄 말줄임 + `title` 전문이 유일한 수단이다. */
const Clamp = styled(Td)`
  overflow: hidden;
  text-overflow: ellipsis;
`;

/**
 * 칸 폭. `table-layout: fixed`는 폭을 균등 분배하므로 그대로 두면 36자 UUID 칸이 여러 줄로
 * 접혀 "버전 윗줄 2줄 이내"가 깨진다(계획 AB-1). 앞 세 칸을 고정하고 검수자가 남는 폭을 받는다.
 * **`VERSION_COLUMNS`와 같은 길이여야 한다** — 아래 `<colgroup>`이 그 배열을 순회하므로 칸 수는
 * 저절로 맞고, 여기서 모자란 자리는 `undefined`(= 자동 폭)가 된다.
 */
const COLUMN_WIDTHS: Array<number | undefined> = [
  44, // 버전 — "v12"까지
  110, // 상태 — "승인됨(은퇴)"가 잘리지 않아야 한다. 은퇴 여부는 이 절의 핵심 신호다.
  172, // 은퇴 — `2026. 09. 22. 오전 11:39` 전문이 **잘리지 않는** 폭(보정 7a, 12px 기준 실측)
];

const Muted = styled.span`
  color: ${COLOR_SYSTEM.get("Gray")?.[400]};
`;

const StateCell = styled.td`
  padding: 16px 8px;
  text-align: center;
  color: ${COLOR_SYSTEM.get("Gray")?.[400]};
`;

/** 보조 줄(전폭). 묶음의 마지막 줄이라 구분선을 여기에 그린다. */
const AuxTd = styled.td`
  padding: 0 8px 7px;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[15]};
`;

const AuxLine = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  line-height: 1.45;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};

  & + & {
    margin-top: 2px;
  }
`;

const AuxLabel = styled.span`
  flex: none;
  color: ${COLOR_SYSTEM.get("Gray")?.[400]};
  font-weight: 400;
`;

/**
 * 보조 줄의 값 — **최대 2줄 clamp**(보정 7a). 전문은 호출부가 `title`에 싣는다.
 *
 * `min-width: 0`이 없으면 flex 항목의 기본 `min-width: auto` 때문에 긴 한 덩어리가 칸을
 * 밀어내 표 래퍼에 가로 스크롤이 생긴다(AC "표 래퍼 scrollWidth <= clientWidth").
 * `overflow-wrap: anywhere`는 공백 없는 500자 한글·UUID를 줄바꿈시킨다.
 */
const AuxValue = styled.span`
  flex: 1;
  min-width: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
`;

/** `×N` — **뜻을 title로 설명한다**(보정 7e). 글자만 보면 "N번 은퇴했다"로 읽힌다. */
const RepeatMark = styled.span`
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
`;

/**
 * 사유 칸의 내용. **세 상태가 모양으로 구분된다**(설계 결정 5 · 보정 7d).
 *
 * **현재 줄에서는 호출되지 않는다** — 그 줄은 사유 줄을 통째로 렌더하지 않는다(보정 7e′ 후속).
 */
const renderReason = ({
  isNotesError,
  isNotesLoading,
  entries,
}: {
  isNotesError: boolean;
  isNotesLoading: boolean;
  entries: RetireReasonEntry[];
}): React.ReactNode => {
  if (isNotesError) return <Muted>—</Muted>;
  if (isNotesLoading) return <Muted>{NOTES_LOADING_TEXT}</Muted>;
  return entries.map((entry, i) => (
    // 노트 id가 없을 수 있어 index를 키로 쓴다(서버가 준 순서가 곧 정체성).
    // eslint-disable-next-line react/no-array-index-key
    <React.Fragment key={i}>
      {i > 0 ? " · " : null}
      {entry.text}
      {typeof entry.repeatCount === "number" && entry.repeatCount > 1 ? (
        <>
          {" "}
          <RepeatMark
            data-testid="dose-rule-version-repeat"
            title={retireRepeatTitle(entry.repeatCount)}
          >
            ×{entry.repeatCount}
          </RepeatMark>
        </>
      ) : null}
    </React.Fragment>
  ));
};

/**
 * 절 머리 문구 — 한 식으로 고정한다(계획 J-1·O-1·P-3).
 * - 로딩·오류: `룰 버전` (세어진 것이 없으므로 개수도 "없음"도 거짓이다)
 * - 이력 있음: `룰 버전 N개`
 * - 이력 없음: `룰 버전 · 이력 없음`
 */
const summaryText = (count: number, settled: boolean): string => {
  if (!settled) return VERSION_SECTION_TITLE;
  if (count > 1) return `${VERSION_SECTION_TITLE} ${count}개`;
  return `${VERSION_SECTION_TITLE} · ${VERSION_EMPTY_TEXT}`;
};

const DoseRuleVersionList: React.FC<DoseRuleVersionListProps> = ({
  versions,
  isLoading = false,
  isError = false,
  onRetry,
  notes,
  isNotesLoading = false,
  isNotesError = false,
  onNotesRetry,
}) => {
  // 응답 형상이 계약과 달라도 렌더가 throw하지 않아야 한다 — 이 화면 위에 에러 바운더리가
  // 없어 throw하면 상세 전체가 하얘진다(계획 I-3).
  const rows = Array.isArray(versions) ? versions : [];
  const settled = !isLoading && !isError;

  /* 펼침 기본값 — **"이력이 없다고 확정된 때만 접는다."**
   *
   * `userOpen ?? (rows.length > 1)`로 두면 로딩·오류에서도 접히고, `<details>`가 닫히면 실
   * 브라우저에서 **오류 문구와 `다시 시도` 버튼이 숨는다**(e2e 픽스처 ④가 잡았다 — jsdom은
   * `<details>` 접힘을 구현하지 않아 컴포넌트 테스트로는 보이지 않는 부류다. 계획 O-2·AA-1).
   *
   * 그래서 접는 조건은 "정착했고(로딩·오류 아님) 줄이 1개 이하"뿐이다. 이력이 있거나, 아직
   * 모르거나(로딩), 보여줄 오류가 있으면 펴 둔다. 사용자가 한 번 토글하면 그 뜻이 이긴다.
   *
   * 사용자가 접은 뜻은 **그 룰 안에서만** 이어진다 — 룰이 바뀌면 호출부가 `key={ruleId}`로
   * 이 컴포넌트를 갈아 끼워 기본 상태로 되돌린다(보정 7c).
   *
   * `useState(hasHistory)`로 초기값을 잡지 않는 이유는 데이터가 늦게 도착하기 때문이다 —
   * 로딩 중 false에 고정된다. 가까운 선례 `DoseRuleSegmentPanel`은 파생 초기값을 쓰지만
   * 그건 `detail`이 도착한 뒤에만 마운트되기 때문에 맞다(계획 B-5·R-1).
   */
  const [userOpen, setUserOpen] = useState<boolean | null>(null);
  const autoOpen = !(settled && rows.length <= 1) || isNotesError;
  const open = userOpen === null ? autoOpen : userOpen;

  /* `toggle` 이벤트는 **사용자 클릭만이 아니라 React가 `open` 속성을 바꿀 때도** 발화한다.
   * 그래서 `setUserOpen(e.currentTarget.open)`을 그대로 쓰면, 로딩 중 auto-open이 만든
   * 프로그램적 toggle이 `userOpen = true`를 영구 고정해 **데이터가 도착해도 절이 접히지
   * 않는다**(그리고 데이터 도착과 이벤트 발화의 경합이라 e2e가 간헐적으로만 깨졌다).
   *
   * 판정 기준: DOM 상태가 auto가 낼 값과 **같으면** 사용자 의사가 없는 것으로 보고 `null`로
   * 되돌린다. 다르면 그건 사용자가 만든 상태다. 이 규칙은 자기 치유적이다 — auto가 뒤집힐
   * 때마다 프로그램적 toggle이 override를 지운다(계획 AB-2). */
  const handleToggle = (e: React.SyntheticEvent<HTMLDetailsElement>) => {
    const next = e.currentTarget.open;
    setUserOpen(next === autoOpen ? null : next);
  };

  const reasonMap = buildRetireReasonMap(notes);

  return (
    <Fold
      data-testid="dose-rule-version-list"
      open={open}
      onToggle={handleToggle}
    >
      <summary>{summaryText(rows.length, settled)}</summary>

      {isError ? (
        <ErrorLine>
          <span>{VERSION_LOAD_ERROR_TEXT}</span>
          {onRetry ? (
            <Button
              buttonType="button"
              type="Text"
              size="XSmall"
              variant="Outlined"
              color="Secondary"
              onClick={onRetry}
            >
              {RETRY_TEXT}
            </Button>
          ) : null}
        </ErrorLine>
      ) : null}

      {/* 노트만 실패한 경우 — 버전 줄은 그대로 보이고 사유 칸만 `—`가 된다. */}
      {isNotesError ? (
        <ErrorLine>
          <span>{NOTES_LOAD_ERROR_TEXT}</span>
          {onNotesRetry ? (
            <Button
              buttonType="button"
              type="Text"
              size="XSmall"
              variant="Outlined"
              color="Secondary"
              onClick={onNotesRetry}
            >
              {RETRY_TEXT}
            </Button>
          ) : null}
        </ErrorLine>
      ) : null}

      {isError ? null : (
        <TableWrap data-testid="dose-rule-version-table-wrap">
          <Table>
            {/* 칸 수를 `VERSION_COLUMNS`에서 파생시킨다 — 폭 배열을 직접 나열하면 헤더를
                고칠 때 어긋나는 두 번째 진실이 생긴다(계획 P-4와 같은 이유). 파생이므로
                "칸 수가 헤더 수와 같다"는 구조적으로 보장되고 별도 테스트가 필요 없다
                (`<col>`은 ARIA role이 없어 RTL로 조회할 수도 없다). */}
            <colgroup>
              {VERSION_COLUMNS.map((col, i) => (
                <col
                  key={col}
                  style={COLUMN_WIDTHS[i] ? { width: COLUMN_WIDTHS[i] } : undefined}
                />
              ))}
            </colgroup>
            <thead>
              <tr>
                {VERSION_COLUMNS.map((col) => (
                  <Th key={col} scope="col">
                    {col}
                  </Th>
                ))}
              </tr>
            </thead>
            {isLoading ? (
              <tbody>
                <tr>
                  <StateCell colSpan={VERSION_COLUMNS.length}>
                    <Spinner />
                  </StateCell>
                </tr>
              </tbody>
            ) : (
              rows.map((row, index) => {
                const current = isCurrentVersionRow(row);
                const note =
                  typeof row?.reviewNote === "string" && row.reviewNote.trim()
                    ? row.reviewNote
                    : null;
                /* 현재 줄의 사유는 **조회조차 하지 않는다**(보정 7e′). 현재 버전은 정의상
                   은퇴하지 않았으므로 은퇴 사유가 없다. 사유 매핑이 행 id로만 붙어 있어
                   `--restore` 직후나 전량 러너의 대체 보관 노트가 현재 행에 남으면 옛 사유가
                   현재 줄에 뜰 수 있는데, 이 규칙이 그것을 구조적으로 막는다
                   (설계 결정 6 불변식 붕괴 경로). */
                const entries = current ? [] : retireReasonEntries(reasonMap, row?.id);
                const reasonPlain = current ? "" : retireReasonText(reasonMap, row?.id);
                return (
                  <Group
                    // id가 없을 수 있어(optional) index를 폴백 키로 둔다.
                    key={typeof row?.id === "number" ? row.id : `idx-${index}`}
                    data-testid="dose-rule-version-group"
                  >
                    <Row data-testid="dose-rule-version-row" $current={current}>
                      <Td>{formatVersionNumber(row?.version)}</Td>
                      <Td>
                        {current ? (
                          versionStatusLabel(row?.status, true)
                        ) : (
                          <Muted>{versionStatusLabel(row?.status, false)}</Muted>
                        )}
                      </Td>
                      {/* 값은 반드시 formatDateTime을 통과한다 — ISO를 잘라 쓰면 9시간
                          어긋난다. 보정 7a로 이 칸은 전문이 들어가는 폭을 갖는다. */}
                      <TimeTd>
                        {current ? VERSION_CURRENT_TEXT : formatDateTime(row?.retiredAt)}
                      </TimeTd>
                      <Clamp title={row?.reviewedBy || undefined}>
                        {row?.reviewedBy || <Muted>—</Muted>}
                      </Clamp>
                    </Row>

                    {/* 보조 줄 — 전폭. 메모·사유가 각각 2줄까지 읽힌다(보정 7a). */}
                    <tr data-testid="dose-rule-version-aux-row">
                      <AuxTd
                        colSpan={VERSION_COLUMNS.length}
                        data-testid="dose-rule-version-aux-cell"
                      >
                        <AuxLine>
                          <AuxLabel>{VERSION_NOTE_LABEL}</AuxLabel>
                          <AuxValue
                            data-testid="dose-rule-version-note"
                            title={note ?? undefined}
                          >
                            {note ?? <Muted>{VERSION_NOTE_EMPTY_TEXT}</Muted>}
                          </AuxValue>
                        </AuxLine>
                        {/* 현재 줄에는 **사유 줄을 라벨째 만들지 않는다**(보정 7e′ 후속).
                            현재 버전은 정의상 은퇴하지 않았으므로 이 칸은 영영 빈다 — 값 없는
                            라벨만 남기면 "사유가 있어야 하는데 없다"로 읽힌다. 메모 줄은 그대로
                            둔다(`메모 없음`은 읽을 값이 있는 사실이다).
                            은퇴 줄의 세 상태: 조회 중 `…` / 실패 `—` / 사유 있음·빈 칸 */}
                        {current ? null : (
                          <AuxLine>
                            <AuxLabel>{VERSION_REASON_LABEL}</AuxLabel>
                            <AuxValue
                              data-testid="dose-rule-version-reason"
                              title={
                                isNotesError ? NOTES_LOAD_ERROR_TEXT : reasonPlain || undefined
                              }
                            >
                              {renderReason({ isNotesError, isNotesLoading, entries })}
                            </AuxValue>
                          </AuxLine>
                        )}
                      </AuxTd>
                    </tr>
                  </Group>
                );
              })
            )}
          </Table>
        </TableWrap>
      )}
    </Fold>
  );
};

export default DoseRuleVersionList;
